import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import LandingPage from "@/pages/LandingPage";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import SignUpWizard from "@/pages/SignUpWizard";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import EmployeeDashboard from "@/pages/EmployeeDashboard";
import EmployeeHistory from "@/pages/EmployeeHistory";
import EmployeeRoster from "@/pages/EmployeeRoster";
import EmployeeProfile from "@/pages/EmployeeProfile";
import SuperAdminDashboardNew from "@/pages/SuperAdminDashboardNew";
import CompanyManagement from "@/pages/CompanyManagement";
import SupportTickets from "@/pages/SupportTickets";
import SupportTicketDetail from "@/pages/SupportTicketDetail";
import CompanyAdminDashboard from "@/pages/CompanyAdminDashboard";
import EmployeeManagement from "@/pages/EmployeeManagement";
import OfficeManagement from "@/pages/OfficeManagement";
import Reports from "@/pages/Reports";
import ManagerControls from "@/pages/ManagerControls";
import RosterBuilder from "@/pages/RosterBuilder";
import CompanySettings from "@/pages/CompanySettings";
import HelpCenter from "@/pages/HelpCenter";
import BillingPage from "@/pages/BillingPage";
import TimesheetPage from "@/pages/TimesheetPage";
import AuditLogsPage from "@/pages/AuditLogsPage";
import ProjectsPage from "@/pages/ProjectsPage";
import SecuritySettings from "@/pages/SecuritySettings";
import PrivacyCenter from "@/pages/PrivacyCenter";
import BackupManagement from "@/pages/BackupManagement";
import ErrorLogsPage from "@/pages/ErrorLogsPage";
import TelegramBotSetup from "@/pages/TelegramBotSetup";
import AIChatWidget from "@/components/AIChatWidget";
import InstallPWA from "@/components/InstallPWA";
import AnnouncementBanner from "@/components/AnnouncementBanner";

const ProtectedRoute = ({ children, allowedRoles = [], allowManagers = false }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Check if user is a manager (either role="manager" OR employee with is_manager flag)
  const isManager = user.role === 'manager' || (user.role === 'employee' && user.is_manager);
  
  // If managers are allowed and user is a manager, grant access
  if (allowManagers && isManager) {
    return children;
  }
  
  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    // Redirect to appropriate dashboard
    if (user.role === "super_admin") return <Navigate to="/super-admin" replace />;
    if (user.role === "company_admin") return <Navigate to="/admin" replace />;
    // Managers go to admin dashboard, regular employees go to employee dashboard
    if (isManager) return <Navigate to="/admin" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" />
      </div>
    );
  }

  const getDefaultRoute = () => {
    if (!user) return "/login";
    if (user.role === "super_admin") return "/super-admin";
    if (user.role === "company_admin") return "/admin";
    // Managers go to admin dashboard (either role="manager" or employee with is_manager)
    if (user.role === "manager" || (user.role === "employee" && user.is_manager)) return "/admin";
    return "/dashboard";
  };
  
  return (
    <Routes>
      {/* Landing page - Public */}
      <Route path="/" element={<LandingPage />} />
      
      {/* Public routes */}
      <Route 
        path="/login" 
        element={user ? <Navigate to={getDefaultRoute()} replace /> : <Login />} 
      />
      <Route 
        path="/register" 
        element={user ? <Navigate to={getDefaultRoute()} replace /> : <Register />} 
      />
      <Route 
        path="/signup" 
        element={user ? <Navigate to={getDefaultRoute()} replace /> : <SignUpWizard />} 
      />
      <Route 
        path="/verify-email" 
        element={<VerifyEmailPage />} 
      />
      <Route 
        path="/forgot-password" 
        element={user ? <Navigate to={getDefaultRoute()} replace /> : <ForgotPassword />} 
      />
      <Route 
        path="/reset-password" 
        element={user ? <Navigate to={getDefaultRoute()} replace /> : <ResetPassword />} 
      />
      
      {/* Super Admin routes */}
      <Route 
        path="/super-admin" 
        element={
          <ProtectedRoute allowedRoles={["super_admin"]}>
            <SuperAdminDashboardNew />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/super-admin/companies" 
        element={
          <ProtectedRoute allowedRoles={["super_admin"]}>
            <CompanyManagement />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/super-admin/support" 
        element={
          <ProtectedRoute allowedRoles={["super_admin"]}>
            <SupportTickets />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/super-admin/support/:ticketId" 
        element={
          <ProtectedRoute allowedRoles={["super_admin"]}>
            <SupportTicketDetail />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/super-admin/backups" 
        element={
          <ProtectedRoute allowedRoles={["super_admin"]}>
            <BackupManagement />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/super-admin/error-logs" 
        element={
          <ProtectedRoute allowedRoles={["super_admin"]}>
            <ErrorLogsPage />
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/admin/telegram-bot" 
        element={
          <ProtectedRoute allowedRoles={["company_admin"]} allowManagers={true}>
            <TelegramBotSetup />
          </ProtectedRoute>
        } 
      />
      
      {/* Company Admin routes - Dashboard and Roster accessible by managers */}
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute allowedRoles={["company_admin"]} allowManagers={true}>
            <CompanyAdminDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/employees" 
        element={
          <ProtectedRoute allowedRoles={["company_admin"]}>
            <EmployeeManagement />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/offices" 
        element={
          <ProtectedRoute allowedRoles={["company_admin"]}>
            <OfficeManagement />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/reports" 
        element={
          <ProtectedRoute allowedRoles={["company_admin"]} allowManagers={true}>
            <Reports />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/controls" 
        element={
          <ProtectedRoute allowedRoles={["company_admin"]}>
            <ManagerControls />
          </ProtectedRoute>
        } 
      />
      
      {/* Admin Roster route - accessible by managers */}
      <Route 
        path="/admin/roster" 
        element={
          <ProtectedRoute allowedRoles={["company_admin"]} allowManagers={true}>
            <RosterBuilder />
          </ProtectedRoute>
        } 
      />

      {/* Admin Timesheet route - accessible by managers */}
      <Route 
        path="/admin/timesheet" 
        element={
          <ProtectedRoute allowedRoles={["company_admin"]} allowManagers={true}>
            <TimesheetPage />
          </ProtectedRoute>
        } 
      />

      {/* Admin Settings route - NOT accessible by managers */}
      <Route 
        path="/admin/settings" 
        element={
          <ProtectedRoute allowedRoles={["company_admin"]}>
            <CompanySettings />
          </ProtectedRoute>
        } 
      />

      {/* Admin Billing route - NOT accessible by managers */}
      <Route 
        path="/admin/billing" 
        element={
          <ProtectedRoute allowedRoles={["company_admin"]}>
            <BillingPage />
          </ProtectedRoute>
        } 
      />

      {/* Help Center - accessible by company admins and managers */}
      <Route 
        path="/admin/help" 
        element={
          <ProtectedRoute allowedRoles={["company_admin"]} allowManagers={true}>
            <HelpCenter />
          </ProtectedRoute>
        } 
      />

      {/* Projects - accessible by company admins and managers */}
      <Route 
        path="/admin/projects" 
        element={
          <ProtectedRoute allowedRoles={["company_admin"]} allowManagers={true}>
            <ProjectsPage />
          </ProtectedRoute>
        } 
      />

      {/* Audit Logs - accessible by company admins and managers */}
      <Route 
        path="/admin/audit-logs" 
        element={
          <ProtectedRoute allowedRoles={["company_admin"]} allowManagers={true}>
            <AuditLogsPage />
          </ProtectedRoute>
        } 
      />

      {/* Security Settings - accessible by all authenticated users */}
      <Route 
        path="/security" 
        element={
          <ProtectedRoute allowedRoles={["employee", "company_admin", "super_admin"]} allowManagers={true}>
            <SecuritySettings />
          </ProtectedRoute>
        } 
      />

      {/* Privacy Center - accessible by all authenticated users */}
      <Route 
        path="/privacy" 
        element={
          <ProtectedRoute allowedRoles={["employee", "company_admin", "super_admin"]} allowManagers={true}>
            <PrivacyCenter />
          </ProtectedRoute>
        } 
      />
      
      {/* Employee routes - Managers and Company Admins can also access for clock-in/out */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute allowedRoles={["employee", "company_admin"]} allowManagers={true}>
            <EmployeeDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/history" 
        element={
          <ProtectedRoute allowedRoles={["employee", "company_admin"]} allowManagers={true}>
            <EmployeeHistory />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/employee/roster" 
        element={
          <ProtectedRoute allowedRoles={["employee", "company_admin"]} allowManagers={true}>
            <EmployeeRoster />
          </ProtectedRoute>
        } 
      />
      
      {/* Profile/Settings - accessible by all authenticated users */}
      <Route 
        path="/profile" 
        element={
          <ProtectedRoute allowedRoles={["employee", "company_admin", "manager"]} allowManagers={true}>
            <EmployeeProfile />
          </ProtectedRoute>
        } 
      />
      
      {/* Default redirect */}
      <Route path="/" element={<Navigate to={getDefaultRoute()} replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  // Register service worker for PWA
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('/service-worker.js')
          .then((registration) => {
            console.log('SW registered:', registration.scope);
          })
          .catch((error) => {
            console.log('SW registration failed:', error);
          });
      });
    }
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="app-container bg-slate-50">
          <AuthenticatedAnnouncements />
          <AppRoutes />
          <Toaster position="top-right" richColors />
          <AuthenticatedChatWidget />
          <InstallPWA />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

// Announcement banner for logged-in users (shows on all authenticated pages)
function AuthenticatedAnnouncements() {
  const { user } = useAuth();
  const isPublicPage = ['/', '/login', '/register', '/signup', '/forgot-password', '/reset-password', '/verify-email'].includes(window.location.pathname);
  
  if (!user || isPublicPage) return null;
  return <AnnouncementBanner />;
}

// Chat widget for logged-in users (only shows on non-landing pages)
function AuthenticatedChatWidget() {
  const { user } = useAuth();
  const isLandingPage = window.location.pathname === '/';
  
  // Don't show on landing page (it has its own widget) or if not logged in
  if (isLandingPage || !user) return null;
  
  return <AIChatWidget context="app" user={user} />;
}

export default App;
